import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "ecentra_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: API });

export const authApi = axios.create({ baseURL: API });
authApi.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Resolve an image URL: external URLs & data URIs pass through; relative
// backend media paths ("/api/media/...") get prefixed with the backend host.
export const resolveImg = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

// Two-level merge so components always receive fully-populated content.
export const mergeContent = (base, over) => {
  if (!over) return base;
  const out = { ...base };
  const keys = new Set([...Object.keys(base), ...Object.keys(over)]);
  keys.forEach((k) => {
    const b = base[k];
    const o = over[k];
    if (o == null) {
      out[k] = b;
    } else if (Array.isArray(b) || Array.isArray(o)) {
      out[k] = o != null ? o : b;
    } else if (typeof b === "object" && typeof o === "object") {
      out[k] = { ...b, ...o };
    } else {
      out[k] = o;
    }
  });
  return out;
};

// ---- Public API ----
export const fetchContent = () => api.get("/content").then((r) => r.data);
export const fetchProducts = () => api.get("/products").then((r) => r.data);
export const fetchScience = () => api.get("/science").then((r) => r.data);
export const fetchScienceArticle = (slug) => api.get(`/science/${slug}`).then((r) => r.data);
export const submitLead = (payload) => api.post("/leads", payload).then((r) => r.data);
export const submitOrder = (payload) => api.post("/orders", payload).then((r) => r.data);

// ---- Admin: auth ----
export const adminLogin = (payload) => api.post("/admin/login", payload).then((r) => r.data);
export const adminMe = () => authApi.get("/admin/me").then((r) => r.data);
export const adminStats = () => authApi.get("/admin/stats").then((r) => r.data);

// ---- Admin: leads & orders ----
export const adminLeads = (status) => authApi.get("/admin/leads", { params: { status } }).then((r) => r.data);
export const adminOrders = (status) => authApi.get("/admin/orders", { params: { status } }).then((r) => r.data);
export const updateLeadStatus = (id, status) => authApi.patch(`/admin/leads/${id}`, { status }).then((r) => r.data);
export const updateOrderStatus = (id, status) => authApi.patch(`/admin/orders/${id}`, { status }).then((r) => r.data);

// ---- Admin: content ----
export const adminUpdateContent = (content) => authApi.put("/admin/content", content).then((r) => r.data);
export const adminResetContent = () => authApi.post("/admin/content/reset").then((r) => r.data);
export const adminUndoResetContent = () => authApi.post("/admin/content/undo-reset").then((r) => r.data);

// ---- Admin: settings (Telegram notifications) ----
export const adminGetSettings = () => authApi.get("/admin/settings").then((r) => r.data);
export const adminUpdateSettings = (settings) => authApi.put("/admin/settings", settings).then((r) => r.data);
export const adminTestTelegram = () => authApi.post("/admin/settings/test-telegram").then((r) => r.data);

// ---- Admin: media ----
export const uploadMedia = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return authApi.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// ---- Admin: products ----
export const adminCreateProduct = (p) => authApi.post("/admin/products", p).then((r) => r.data);
export const adminUpdateProduct = (id, p) => authApi.put(`/admin/products/${id}`, p).then((r) => r.data);
export const adminDeleteProduct = (id) => authApi.delete(`/admin/products/${id}`).then((r) => r.data);

// ---- Admin: science articles ----
export const adminListScience = () => authApi.get("/admin/science").then((r) => r.data);
export const adminCreateScience = (a) => authApi.post("/admin/science", a).then((r) => r.data);
export const adminUpdateScience = (id, a) => authApi.put(`/admin/science/${id}`, a).then((r) => r.data);
export const adminDeleteScience = (id) => authApi.delete(`/admin/science/${id}`).then((r) => r.data);

// ---- Admin: data health check ----
export const adminHealth = () => authApi.get("/admin/health").then((r) => r.data);
export const adminHealthFix = () => authApi.post("/admin/health/fix").then((r) => r.data);

// ---- Sales / proof ----
export const adminSalesMonthly = () => authApi.get("/admin/sales/monthly").then((r) => r.data);

// ---- Admin: AI composite generation ----
export const adminGenerateComposite = (payload) => authApi.post("/admin/generate-composite", payload).then((r) => r.data);

export const uploadProof = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/upload/proof", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const downloadCsv = async (kind) => {
  const res = await authApi.get(`/admin/export/${kind}.csv`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = `ecentra_${kind}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const formatPHP = (value) => {
  try {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  } catch (e) {
    return `\u20b1${value}`;
  }
};
